import { rootApi } from "../../app/apis/api.root";
import { API_TAGS } from "../../app/constants";
import { API_URL } from "../../constants/urls.constants";
import { CommonResponse, GetQueryArgsType } from "../../types";
import {
  BankDetails,
  ServiceTypeDetails,
  ServiceTypesParams,
  VendorActivityLog,
  VendorDetails,
  VendorList,
  VendorListParams,
} from "./vendor.type";

const { VENDOR_LIST, SERVICE_TYPES } = API_TAGS || {};

export const vendorApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    // Mutations
    createVendor: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.createVendor,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [VENDOR_LIST],
    }),

    updateVendor: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.updateVendor}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: (_, __, id) => [VENDOR_LIST, { type: VENDOR_LIST, id }],
    }),

    archiveVendor: builder.mutation({
      query: ({ id }) => {
        return {
          url: `${API_URL.archiveVendor}/${id}`,
          method: "PATCH",
        };
      },
      invalidatesTags: (_, __, id) => [VENDOR_LIST, { type: VENDOR_LIST, id }],
    }),

    setDefaultPaymentInfo: builder.mutation({
      query: ({ paymentInfoId, vendorId }) => ({
        url: `${API_URL.setDefaultPaymentInfo}/${paymentInfoId}/${vendorId}`,
        method: "PATCH",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: VENDOR_LIST, id: arg.vendorId },
        VENDOR_LIST,
      ],
    }),

    updatePaymentInfo: builder.mutation({
      query: ({ paymentInfoId, vendorId, data }) => ({
        url: `${API_URL.updateDefaultPaymentInfo}/${paymentInfoId}/${vendorId}`,
        method: "PATCH",
        body: data,
      }),
      invalidatesTags: (_, __, arg) => [
        { type: VENDOR_LIST, id: arg.vendorId },
        VENDOR_LIST,
      ],
    }),

    deletePaymentInfo: builder.mutation({
      query: ({ paymentInfoId, vendorId }) => ({
        url: `${API_URL.deleteVendorPaymentInfo}/${paymentInfoId}/${vendorId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_, __, arg) => [
        { type: VENDOR_LIST, id: arg.vendorId },
        VENDOR_LIST,
      ],
    }),

    createServiceType: builder.mutation({
      query: (data) => {
        return {
          url: API_URL.createServiceType,
          method: "POST",
          body: data,
        };
      },
      invalidatesTags: [SERVICE_TYPES],
    }),

    updateServiceType: builder.mutation({
      query: ({ data, id }) => {
        return {
          url: `${API_URL.updateServiceType}/${id}`,
          method: "PATCH",
          body: data,
        };
      },
      invalidatesTags: [SERVICE_TYPES],
    }),

    // Queries
    getVendorList: builder.query<
      CommonResponse<VendorList>,
      GetQueryArgsType<VendorListParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getVendorList,
        ...queryArgs,
      }),
      providesTags: [VENDOR_LIST],
    }),

    getVendorDetails: builder.query<CommonResponse<VendorDetails>, number>({
      query: (id) => ({
        url: `${API_URL.getVendorDetails}/${id}`,
      }),
      providesTags: (_, __, id) => [{ type: VENDOR_LIST, id }],
    }),

    getServiceTypesList: builder.query<
      CommonResponse<ServiceTypeDetails[]>,
      GetQueryArgsType<ServiceTypesParams>
    >({
      query: (queryArgs) => ({
        url: API_URL.getServiceTypeList,
        ...queryArgs,
      }),
      providesTags: [SERVICE_TYPES],
    }),

    getBankList: builder.query<
      CommonResponse<BankDetails[]>,
      GetQueryArgsType<{ type: string; status: string }>
    >({
      query: (queryArgs) => ({
        url: API_URL.getBankList,
        ...queryArgs,
      }),
      providesTags: [SERVICE_TYPES],
    }),

    getVendorActivityLog: builder.query<
      CommonResponse<VendorActivityLog>,
      {
        id: string;
        params: { page: number; limit: number };
      }
    >({
      query: ({ id, params }) => {
        return {
          url: `${API_URL.getVendorActivityLog}/${id}`,
          params,
        };
      },
      providesTags: [{ type: VENDOR_LIST, id: "activity-log" }],
    }),
  }),
});

export const {
  // Mutations
  useCreateVendorMutation,
  useUpdateVendorMutation,
  useArchiveVendorMutation,
  useSetDefaultPaymentInfoMutation,
  useUpdatePaymentInfoMutation,
  useDeletePaymentInfoMutation,
  useCreateServiceTypeMutation,
  useUpdateServiceTypeMutation,

  // Queries
  useGetVendorListQuery,
  useGetVendorDetailsQuery,
  useGetServiceTypesListQuery,
  useGetBankListQuery,
  useGetVendorActivityLogQuery,
} = vendorApi;
